import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { adminApi, tokenStore } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = anon, obj = user
  const [ready, setReady] = useState(false);

  const bootstrap = useCallback(async () => {
    try {
      const u = await adminApi.me();
      setUser(u);
    } catch (err) {
      // 401 is expected when not authenticated; surface only unexpected errors in dev.
      if (process.env.NODE_ENV !== "production" && err?.response?.status && err.response.status !== 401) {
        console.error("Auth bootstrap failed:", err);
      }
      tokenStore.clear();
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  async function login(email, password) {
    const data = await adminApi.login(email, password);
    // Server sets httpOnly cookie; localStorage Bearer is only a fallback for
    // browsers that block third-party cookies. Same-origin uses cookies.
    if (data.access_token) tokenStore.set(data.access_token);
    setUser(data.user);
    return data.user;
  }

  async function logout() {
    try {
      await adminApi.logout();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout request failed (clearing client state anyway):", err?.message || err);
      }
    }
    tokenStore.clear();
    setUser(false);
  }

  const value = useMemo(() => ({ user, ready, login, logout }), [user, ready]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
